import * as yup from 'yup';
import { dropDownInputs, radioInputs, textInputs } from '@/pages/CandidateInfo/candidateInfoFields';
import candidatesServices from '@/services/candidates';
import { CandidateTechStacksType, QuestionType } from '@/types';
import { getUrlObject } from './global';

const URLRegex =
  /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+(:[0-9]+)?|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/;

const requiredMessage = 'This field is required.';
export const incorrectUrlMessage = 'Enter a valid url.';
const atLeastOneOption = 'Select at least 1 option.';

const getLinkedinUserName = (url: string): string => {
  try {
    const { pathname } = getUrlObject(url);
    if (!pathname) {
      return '';
    }
    const withoutPrefix = pathname.replace(/in\//, '');
    const splittedPathnames = withoutPrefix.split('/').filter(Boolean);
    const lastPathname = splittedPathnames.pop();
    return lastPathname ?? '';
  } catch {
    return '';
  }
};

const validateLinkedinUrl = async (url): Promise<boolean> => {
  try {
    const linkedinUsername = getLinkedinUserName(url);

    if (!linkedinUsername) {
      return true;
    }

    const filters = [
      {
        field: 'linkedinUrl',
        condition: 'stringContain',
        value: [linkedinUsername ?? ''],
      },
    ];

    const response = await candidatesServices.getFilteredCandidates({
      take: 10,
      page: 1,
      column: 'firstName',
      order: 'ASC',
      filters: JSON.stringify(filters),
    });

    const candidates = response?.data?.data;
    const { pathname } = window.location;

    const isLinkedinUrlUsed = candidates.some((candidate) => {
      const isTheSameUser = pathname.includes(candidate.id);
      const linkedinPathname = getLinkedinUserName(candidate?.linkedinUrl);
      const doesCandidateUrlMatch = linkedinPathname === linkedinUsername;
      return doesCandidateUrlMatch && !isTheSameUser;
    });

    return !isLinkedinUrlUsed;
  } catch {
    return false;
  }
};
const urlDoesntContainUsername = (value): boolean => {
  if (!value) {
    return true;
  }
  const { pathname } = getUrlObject(value);
  const linkedinUsername = pathname && pathname?.replace(/in\/|\//g, '');

  return !!linkedinUsername;
};

const isEmptyOrValidUrl = (value): boolean => (value ? URLRegex.test(value) : true);

export const technicalValidationProjectSchema = yup.object().shape({
  githubUrl: yup.string().test('is-url-or-empty', incorrectUrlMessage, isEmptyOrValidUrl),
});

export const linkedinUrlSchemaWithoutValidation = yup
  .string()
  .matches(URLRegex, incorrectUrlMessage)
  .test('is-a-linkedin-url', 'Invalid Linkedin URL', (value): boolean => {
    return !value || value.includes('linkedin');
  })
  .test('does-it-have-username', 'Invalid Linkedin URL', urlDoesntContainUsername);

export const linkedinUrlSchema = linkedinUrlSchemaWithoutValidation.test(
  'validate-linkedin-url',
  'Linkedin already used.',
  validateLinkedinUrl
);

export const candidateSchema = yup.object().shape({
  firstName: yup.string().required(requiredMessage),
  lastName: yup.string().required(requiredMessage),
  email: yup.string().email('Invalid e-mail address.'),
  source: yup.string().required(requiredMessage),
  country: yup.string().required(requiredMessage),
  developerType: yup.string().required(requiredMessage),
  mainTechStack: yup.array().min(1, atLeastOneOption).required(requiredMessage),
  githubUrl: yup.string().test('is-url-or-empty', incorrectUrlMessage, isEmptyOrValidUrl),
  trioProfileUrl: yup.string().test('is-url-or-empty', incorrectUrlMessage, isEmptyOrValidUrl),
  linkedinUrl: linkedinUrlSchemaWithoutValidation.required(requiredMessage),
  hasFintechExperience: yup.string().required(requiredMessage),
});

export const expertiseSchema = yup.object().shape({
  skillId: yup.string().required('Please select an expertise'),
  experience: yup.string().nullable(),
  description: yup.string().nullable(),
  isMainStack: yup.boolean().nullable(),
});

export const getInputType = (name: string): QuestionType => {
  const isDropdown = dropDownInputs.includes(name);
  const isRadio = radioInputs.includes(name);
  const isText = textInputs.includes(name);

  if (isText) {
    return QuestionType.text;
  }

  if (isDropdown) {
    return QuestionType.dropdown;
  }

  if (isRadio) {
    return QuestionType.rating;
  }

  throw new Error(`${name} doesn't have a valid QuestionType`);
};

export const getMainTechSkillsFormatted = (skills?: CandidateTechStacksType[]): string[] => {
  if (!skills || !skills.length) {
    return [];
  }

  const filteredMainTechSkills = skills.filter((skill) => skill.isMainStack);
  return filteredMainTechSkills?.map((candidateTech) => candidateTech?.skill?.name);
};
